import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import {
  TabStrip,
  TabStripTab,
  Card,
  CardBody,
} from '@progress/kendo-react-layout';
import { ADMINORGANIZATION } from '../../../../constants/applicationConstants';
import {
  DataTableHoc,
  Loader,
  ErrorFallback,
  deleteFilter,
  applyFilter,
  saveFilters,
} from 'smart-react';
import { GridColumnsList } from '../Components/Filter/FilterElements';
import { UnapprovedGridColumnsList } from '../Components/Filter/UnapprovedFilterElements';
import DataColumns from '../Components/DataColumns/DataColumns';
import UnapprovedDataColumns from '../Components/DataColumns/UnapprovedDataColumns';
import { useAuth } from '../../../Core/Context/AuthContext';
import { isAdminRole } from '../../../../Utils/Auth/ValidatePermissions';
import NoPermission from '../../../Core/NoPermission/NoPermission';
import {
  ROW_LAYOUTS,
  PAGE_SIZE,
} from '../../../../constants/applicationConstants';
const BaseAdminDataTable = React.lazy(
  () => import('../Components/DataTable/DataTable'),
);
const AdminOrganizationDataTable = DataTableHoc(BaseAdminDataTable);

const BaseUnapprovedDataTable = React.lazy(
  () => import('../Components/DataTable/UnapprovedDataTable'),
);
const AdminUnapprovedOrganizationDataTable = DataTableHoc(BaseUnapprovedDataTable);

/**
 * Admin Organization main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Admin Organization component.
 */
const AdminOrganization = () => {
  const location = useLocation();
  const [selected, setSelected] = React.useState(0);
  const [userData, setUserData] = React.useState([]);
  const dataColumnsArray = DataColumns({});
  const unapprovedDataColumnsArray = UnapprovedDataColumns({});
  // used to check API is ready to call
  const [isReady, setIsReady] = React.useState(false);
  const { user } = useAuth();

  // Deep-link to a specific tab via URL hash.
  // Example: /admin/organizations#unapproved-org
  React.useEffect(() => {
    const hash = (location?.hash ?? '').toLowerCase();
    if (hash === '#unapproved-org') {
      setSelected(1);
    } else if (hash === '' || hash === '#organizations') {
      setSelected(0);
    }
  }, [location?.hash]);
  const handleSelect = (e) => {
    setSelected(e.selected);
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto k-p-2'>
        <React.Suspense fallback={<Loader />}>
          <Card className='detail-grid-section k-pt-3'>
            {!isAdminRole(user) && <NoPermission />}
            {isAdminRole(user) && (
              <>
                <TabStrip
                  className='page-tabs'
                  selected={selected}
                  onSelect={handleSelect}
                >
                  <TabStripTab
                    title='Organizations'
                    contentClassName={`tabs-card`}
                  >
                    <Card className={`tabs-card detail-card`}>
                      <CardBody className='!k-px-0'>
                        <div className='card-body'>
                          <div className='container mx-auto k-p-2 add-page-section'>
                            <div className='container mx-auto'>
                              <React.Suspense fallback={<Loader />}>
                                <AdminOrganizationDataTable
                                  deleteFilter={deleteFilter}
                                  applyFilter={applyFilter}
                                  saveFilters={saveFilters}
                                  moduleName={ADMINORGANIZATION}
                                  dataColumns={dataColumnsArray}
                                  gridColumnsList={GridColumnsList}
                                  dataTableName={ADMINORGANIZATION}
                                  setIsReady={setIsReady}
                                  isReady={isReady}
                                  rowLayoutConfigName={`${ADMINORGANIZATION}.DataGrid.RowLayout`}
                                  pageLengthConfigName={`${ADMINORGANIZATION}.DataGrid.ItemsPerPage`}
                                  dataSet={userData}
                                  setDataSet={setUserData}
                                  initialSort={{
                                    field: 'id',
                                    dir: 'desc',
                                  }}
                                  isCardonMobileView={false}
                                  total={process.env.DefaultLimit}
                                  defaultRowLayout={process.env.RowLayout}
                                  rowLayouts={ROW_LAYOUTS}
                                  pageSizes={PAGE_SIZE}
                                  isDefaultSlider={true}
                                  contentSliderTitle={'Organization'}
                                  isActive={true}
                                />
                              </React.Suspense>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </TabStripTab>
                  <TabStripTab
                    title='Unapproved Orgs'
                    contentClassName={`tabs-card`}
                  >
                    <Card className={`tabs-card detail-card`}>
                      <CardBody className='!k-px-0'>
                        <div className='card-body'>
                          <div className='container mx-auto k-p-2 add-page-section'>
                            <div className='container mx-auto'>
                              <React.Suspense fallback={<Loader />}>
                                <AdminUnapprovedOrganizationDataTable
                                  deleteFilter={deleteFilter}
                                  applyFilter={applyFilter}
                                  saveFilters={saveFilters}
                                  moduleName={`${ADMINORGANIZATION}UnApproved`}
                                  dataColumns={unapprovedDataColumnsArray}
                                  gridColumnsList={UnapprovedGridColumnsList}
                                  dataTableName={`${ADMINORGANIZATION}UnApproved`}
                                  setIsReady={setIsReady}
                                  isReady={isReady}
                                  rowLayoutConfigName={`${ADMINORGANIZATION}UnApproved.DataGrid.RowLayout`}
                                  pageLengthConfigName={`${ADMINORGANIZATION}UnApproved.DataGrid.ItemsPerPage`}
                                  dataSet={userData}
                                  setDataSet={setUserData}
                                  initialSort={{
                                    field: 'id',
                                    dir: 'desc',
                                  }}
                                  isCardonMobileView={false}
                                  total={process.env.DefaultLimit}
                                  defaultRowLayout={process.env.RowLayout}
                                  rowLayouts={ROW_LAYOUTS}
                                  pageSizes={PAGE_SIZE}
                                  isDefaultSlider={true}
                                  contentSliderTitle={'Organization'}
                                  isActive={false}
                                />
                              </React.Suspense>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </TabStripTab>
                </TabStrip>
              </>
            )}
          </Card>
        </React.Suspense>
      </div>
    </ErrorBoundary>
  );
};
export default AdminOrganization;
